import React, { Fragment, useEffect, useState } from 'react';
import TechnicalConceptCreateContainer from './TechnicalConceptCreateContainer';
import TechnicalConceptDetailContainer from './TechnicalConceptDetailContainer';
import { ModalType } from '../../../../utils';
import { TechnicalFilter } from '../../types/technical-concept';
import TechnicalConceptView from '../views/TechnicalConceptView';
import { useFetchTechnicalConcept } from '../../../../hooks/technicalConcept/technicalConcept';
import { useSupervisors } from '../../../../share';
import useFormikConceptFilters from '../../../../utils/validators/concept/concept-filter-validators';
import { useCancelRequest } from '../../../../hooks';
import { useCustomToast } from '../../../../components';
import { useAtom } from 'jotai';
import { authAtom } from '../../../../state';
import { modalStateAtom } from '../../../../state/atoms';

const TechnicalConceptContainer: React.FC = () => {
  const [authData] = useAtom(authAtom);

  const [filter, setFilter] = React.useState<TechnicalFilter>({
    requester_id: authData?.user?.id,
  });
  const { data: supervisors } = useSupervisors();
  const [modalState, setModalState] = React.useState<{
    type: ModalType;
    technicalId?: string;
  }>({ type: null, technicalId: undefined });
  const [modalAtom, setModalAtom] = useAtom<boolean>(modalStateAtom)

  const [isModalCustomFilter, setIsModalCustomFilter] =
    useState<boolean>(false);
  const { mutateAsync: cancelRequest } = useCancelRequest();
  const { showToast } = useCustomToast();

  const {
    data: concepts,
    isPending: loadList,
    refetch,
  } = useFetchTechnicalConcept(filter);

  const handleFilter = async (values: Partial<TechnicalFilter>) => {
    setFilter({
      ...values,
      requester_id: authData?.user.id,
      created_at: values.created_at
        ? new Date(values.created_at).toISOString().split('T')[0]
        : '',
    });
  };

  const formikFilter = useFormikConceptFilters({ onSubmit: handleFilter });

  const handleViewCreate = () => {
    setModalState({ type: 'create', technicalId: undefined });
  };

  const handleViewDetail = (id: string) => {
    setModalState({ type: 'detail', technicalId: id });
  };

  const handlePagination = (page: number) => {
    setFilter({
      page: page.toString(),
      requester_id: authData?.user.id,
    });
  };

  const handleModalToggle = (type: ModalType | null, id?: string) => {
    setModalState({
      type: type,
      technicalId: id,
    });
    setModalAtom(false);
  };

  const onCancelRequest = async (id: string, observations: string) => {
    try {
      await cancelRequest({ id, data: { reason: observations } });
      setFilter({
        requester_id: authData?.user.id,
      });
    } catch (error) {
      showToast('error', 'Error al anular solicitud.');
    }
  };

  useEffect(() => {
    refetch();
  }, [filter, refetch]);

  return (
    <Fragment>
      {!loadList && (
        <TechnicalConceptCreateContainer
          isOpen={modalState.type === 'create' || modalAtom}
          OnClose={() => handleModalToggle(null)}
          dataTechnical={concepts}
        />
      )}
      {!loadList && (
        <TechnicalConceptDetailContainer
          isOpen={modalState.type === 'detail'}
          OnClose={() => {
            handleModalToggle(null);
          }}
          technicalId={modalState.technicalId || ''}
          onViewDetail={handleViewDetail}
        />
      )}

      <TechnicalConceptView
        setIsModalCustomFilter={setIsModalCustomFilter}
        isLoading={loadList}
        data={concepts}
        supervisors={supervisors}
        formik={formikFilter}
        onViewDetail={handleViewDetail}
        onViewCreate={handleViewCreate}
        onPagination={handlePagination}
        onResetFilters={() => {
          handleFilter({});
          formikFilter.resetForm();
        }}
        onCancelRequest={(id, observations) =>
          onCancelRequest(id, observations)
        }
      />
    </Fragment>
  );
};

export default TechnicalConceptContainer;
